import React, { FC, useContext, useEffect, useState } from 'react'
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore'
import { Info } from 'lucide-react'

import MainModal from './common/MainModal'
import { CreditCardPaymentMethod } from '../payment/CreditCardPaymentMethod/CreditCardPaymentMethod'
import { MainPaymentMember } from '../payment/PaymentMembers/MainPaymentMember/MainPaymentMember'
import { MarkPaymentMethod } from './components/MarkPaymentMethod'
import { IconBanknotes } from '../icons/IconBanknotes'
import { IconPayPal } from '../icons/IconPayPal'
import { IconCheckBook } from '../icons/IconCheckBook'
import { IconVenmo } from '../icons/IconVenmo'
import { SubPaymentMember } from '../payment/PaymentMembers/SubPaymentMember'

import { HandleModalContext } from '../../layout/mainlayout/MainLayout'
import { getCurrency, getTotalPrices } from '../../helpers/payment'
import { getRegistrationEventDataInDb } from '../../helpers/registration-on-event'
import { getFloatPrice } from '../../helpers/price'
import useToasterHelper from '../../helpers/ToasterHelper'
import { getAllItems } from '../../helpers/getRegistrations'
import { getUserFullName } from '../../helpers/helpers'

import { usePaymentActions } from '../../hooks/usePaymentActions'

import { useAppSelector } from '../../store/hooks'
import { selectProfileData } from '../../store/user/userSlice'

import FirebaseApp from '../../services/firebaseApp'
import EventReviewPublishService from '../../services/firebase-service/event-review-publish/EventReviewPublishService'
import UserService from '../../services/firebase-service/users/userService'

import { UserModel } from '../../models/users/user.model'
import { ISelectedCard, IUserInterface } from '../../models/users/user.interface'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { IRegistrationTicketInterface } from '../../models/registration-tickets/registrationTicket.interface'
import { IRegistrationFeesInterface } from '../../models/registration-fees/registrationFees.interface'
import { IAmountByPerson } from '../../models/event-registered-users/event-registered-users.interface'

import { MODAL_CONSTS } from '../../const/modal-const'
import { CONST } from '../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface PayAllProps {
  dataToPassOn: {
    registrationId: string
    eventId: string
    userId: string
    registrarId: string
    defaultOpenedSecondaryAccordion: string
  }
}

export const PayAll: FC<PayAllProps> = ({ dataToPassOn }) => {
  const { registrationId, eventId, userId, registrarId, defaultOpenedSecondaryAccordion } =
    dataToPassOn

  const { onMarkPaid, onPayByCard } = usePaymentActions()
  const toastFunctions = useToasterHelper()

  const handleModalContext = useContext(HandleModalContext)
  const handleModal = handleModalContext?.handleModal

  const profileData = useAppSelector(selectProfileData)

  const [loading, setLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [user, setUser] = useState<IUserInterface | null>(null)
  const [recipient, setRecipient] = useState<IAmountByPerson | null>(null)
  const [recipients, setRecipients] = useState<IAmountByPerson[]>([])
  const [organizer, setOrganizer] = useState<IUserInterface | null>(null)
  const [eventReviewPublish, setEventReviewPublish] = useState<IEventReviewPublish | null>(null)
  const [selectedCard, setSelectedCard] = useState<ISelectedCard>({
    type: 'cash',
    pmId: '',
    cardNumber: '',
  })
  const [registrationsByDay, setRegistrationsByDay] = useState<IRegistrationByDayInterface[]>([])
  const [registrationsFees, setRegistrationsFees] = useState<IRegistrationFeesInterface[]>([])
  const [registrationsTickets, setRegistrationsTickets] = useState<IRegistrationTicketInterface[]>(
    []
  )
  const [totalAmount, setTotalAmount] = useState<number>(0)

  useEffect(() => {
    const onload = async () => {
      try {
        setLoading(true)
        const eventReviewPublishInDb = await EventReviewPublishService.getById(eventId)
        const organizerInDb = await UserService.getUserById(eventReviewPublishInDb?.owner!)

        const {
          registrationsByDay: registrationsByDayInDb,
          registrationsFees: registrationsFeesInDb,
          registrationsTickets: registrationsTicketsInDb,
        } = await getRegistrationEventDataInDb({
          registrationId: registrationId,
          ownerId: handleModalContext?.activeModalName.includes(MODAL_CONSTS.REGISTRAR_INFO)
            ? null
            : userId,
          type: ['classes', 'fees', 'tickets'],
        })

        const registrationsByDayInDbUnpaid = registrationsByDayInDb.filter(
          (fee) => !fee.paymentStatus && !fee.isScratched
        )
        const registrationsFeesInDbUnpaid = registrationsFeesInDb.filter(
          (fee) => !fee.paymentStatus && !fee.isScratched
        )
        const registrationsTicketsInDbUnpaid = registrationsTicketsInDb.filter(
          (fee) => !fee.paymentStatus && !fee.isScratched
        )

        const { unpaidTotal } = getTotalPrices({
          registrationsEntries: registrationsByDayInDbUnpaid,
          registrationsFees: registrationsFeesInDbUnpaid,
          registrationsTickets: registrationsTicketsInDbUnpaid,
          isChargeSalesTax: eventReviewPublishInDb?.EventPaymentSettings?.chargeSalesTax ?? false,
        })

        const { amountByPerson } = getAllItems({
          registrationsByDay: registrationsByDayInDbUnpaid,
          fees: registrationsFeesInDbUnpaid,
          tickets: registrationsTicketsInDbUnpaid,
          chargeSalesTax: eventReviewPublishInDb?.EventPaymentSettings?.chargeSalesTax || false,
        })

        setRecipient(amountByPerson.find((person) => person.recipientId === userId) || null)
        setRecipients(
          amountByPerson.filter(
            (person) =>
              person.recipientId !== userId &&
              person.recipientId !== registrarId &&
              person.amountToPay > 0
          )
        )

        setTotalAmount(unpaidTotal / 100)
        setRegistrationsByDay(registrationsByDayInDbUnpaid)
        setRegistrationsFees(registrationsFeesInDbUnpaid)
        setRegistrationsTickets(registrationsTicketsInDbUnpaid)
        setEventReviewPublish(eventReviewPublishInDb)
        setOrganizer(organizerInDb)
      } catch (error) {
        console.error('error: ', error)
      } finally {
        setLoading(false)
      }
    }
    onload().then()
  }, [eventId, handleModalContext?.activeModalName, registrarId, registrationId, userId])

  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null

    if (userId) {
      unsubscribe = onSnapshot(
        doc(FirebaseApp.firestore, COLLECTIONS.USERS.NAME, userId),
        (doc) => {
          if (doc.exists()) setUser(UserModel.fromFirestoreDoc(doc))
        }
      )
    }

    return () => {
      unsubscribe?.()
    }
  }, [userId])

  const onUpdate = (
    updatedItems: (
      | IRegistrationByDayInterface
      | IRegistrationFeesInterface
      | IRegistrationTicketInterface
    )[],
    allItems: (
      | IRegistrationByDayInterface
      | IRegistrationFeesInterface
      | IRegistrationTicketInterface
    )[]
  ) => {
    return allItems.map((item) => {
      const current = updatedItems?.find((i) => i.id === item.id)
      if (current?.id) return { ...item, ...current }
      else return item
    })
  }

  const onSubmit = async () => {
    const registrationsByDayUpdated: IRegistrationByDayInterface[] = []
    const registrationsFeesUpdated: IRegistrationFeesInterface[] = []
    const registrationsTicketsUpdated: IRegistrationTicketInterface[] = []

    if (!user) {
      toastFunctions.error({
        message: 'User not found.',
      })
      return
    }

    setButtonLoading(true)

    let response
    if (selectedCard.type === 'card' || selectedCard.type === 'us_bank_account') {
      response = await onPayByCard({
        registrationsByDayItems: registrationsByDay,
        feeItems: registrationsFees,
        ticketItems: registrationsTickets,
        card: selectedCard,
        registrationDocId: registrationId,
        user: user,
        publishedEvent: eventReviewPublish,
      })
    } else {
      response = await onMarkPaid({
        markPaidRegistrationsByDayItems: registrationsByDay,
        markPaidFeesItems: registrationsFees,
        markPaidTicketsItems: registrationsTickets,
        paymentType: selectedCard.type,
        // checkMemo: values.checkMemo,
        // checkNumber: values.checkNumber,
        registrationDocId: registrationId,
        user: user,
        organizerId: organizer?.id || null,
      })
    }

    if (!response) {
      setButtonLoading(false)
      return
    }

    registrationsByDayUpdated.push(...(response?.registrationsByDayUpdated || []))
    registrationsFeesUpdated.push(...(response?.registrationsFeesUpdated || []))
    registrationsTicketsUpdated.push(...(response?.registrationsTicketsUpdated || []))

    setRegistrationsByDay(
      onUpdate(registrationsByDayUpdated, registrationsByDay) as IRegistrationByDayInterface[]
    )
    setRegistrationsFees(
      onUpdate(registrationsFeesUpdated, registrationsFees) as IRegistrationFeesInterface[]
    )
    setRegistrationsTickets(
      onUpdate(registrationsTicketsUpdated, registrationsTickets) as IRegistrationTicketInterface[]
    )

    handleModal?.(false, MODAL_CONSTS.PAY_ALL)

    // Reopening the modal to update data in the modal
    if (handleModalContext?.activeModalName.includes(MODAL_CONSTS.RIDER_INFO)) {
      handleModal?.(false, MODAL_CONSTS.RIDER_INFO)
      setTimeout(() => {
        handleModalContext?.handleModal?.(true, MODAL_CONSTS.RIDER_INFO, {
          ...handleModalContext?.dataToPassOn,
          defaultActiveTab: defaultOpenedSecondaryAccordion,
        })
      }, 100)
    }

    // Reopening the modal to update data in the modal
    if (handleModalContext?.activeModalName.includes(MODAL_CONSTS.REGISTRAR_INFO)) {
      handleModal?.(false, MODAL_CONSTS.REGISTRAR_INFO)
      setTimeout(() => {
        handleModalContext?.handleModal?.(true, MODAL_CONSTS.REGISTRAR_INFO, {
          ...handleModalContext?.dataToPassOn,
          defaultActiveTab: defaultOpenedSecondaryAccordion,
        })
      }, 100)
    }
    setButtonLoading(false)
  }

  const openAddNewCardModal = () => {
    handleModal?.(false, MODAL_CONSTS.PAY_ALL)
    handleModal?.(true, MODAL_CONSTS.CARD, {
      ...dataToPassOn,
      add_card: true,
      re_open_modal: true,
      modal_name: MODAL_CONSTS.PAY_ALL,
      isApproved: profileData?.id === organizer?.id,
      organizerId: eventReviewPublish?.EventDetails?.owner,
      user: user,
    })
  }

  const openAddNewBankModal = () => {
    handleModal?.(false, MODAL_CONSTS.PAY_ALL)
    handleModal?.(true, MODAL_CONSTS.CONNECT_BANK_ACCOUNT, {
      ...dataToPassOn,
      reOpenModalName: MODAL_CONSTS.PAY_ALL,
      user: user,
      organizer: organizer,
    })
  }

  const currency = getCurrency(eventReviewPublish?.EventPaymentSettings?.currency)

  let allowPay = true
  if (selectedCard.type === 'card') {
    allowPay =
      eventReviewPublish?.EventPaymentSettings?.allowCreditCardPayment ||
      eventReviewPublish?.EventPaymentSettings?.allowACHPayment ||
      organizer?.id === profileData.id
  } else if (selectedCard.type === 'us_bank_account') {
    allowPay =
      eventReviewPublish?.EventPaymentSettings?.allowACHPayment || organizer?.id === profileData.id
  }

  return (
    <MainModal
      show={true}
      type="PAY_ALL"
      size="md"
      title="Pay full bill"
      modalClassName="!p-0 !m-0"
      className="!p-0 !m-0"
      titleClassName="pt-6 pb-2 px-4 sm:px-10 text-[25px] font-bold"
      footerClassName="!px-4 sm:!px-10 max-w-full"
      buttons={[
        {
          label: `Pay ${getFloatPrice(`${currency}${totalAmount}`)}`,
          fullWidth: true,
          disabled: loading || !selectedCard.type || totalAmount <= 0 || !allowPay,
          loading: loading || buttonLoading,
          onClick: onSubmit,
        },
        {
          label: 'Cancel',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitIconThemeColor/50',
          fullWidth: true,
          disabled: loading || buttonLoading,
          onClick: () => handleModal?.(false, MODAL_CONSTS.PAY_ALL),
        },
      ]}
    >
      <div className="relative pt-6 px-4 sm:px-10 min-h-1/2 overflow-y-auto flex-1 scrollbar-none">
        <div className="flex flex-col gap-4">
          {user?.id && (
            <MainPaymentMember
              recipient={{
                recipientName: getUserFullName(user) || '',
                recipientPicture: user?.userProfilePicture || '',
                amountToPay: handleModalContext?.activeModalName.includes(
                  MODAL_CONSTS.REGISTRAR_INFO
                )
                  ? recipient?.amountToPay || 0
                  : totalAmount,
              }}
              isSplitBill={true}
              currency={currency}
              totalPrice={totalAmount}
              loading={loading}
            />
          )}
          {recipients?.[0] && (
            <>
              <div className="flex flex-wrap gap-2">
                {recipients?.map((recipient) => (
                  <SubPaymentMember
                    key={recipient.recipientId}
                    recipient={{
                      recipientName: recipient.recipientName,
                      recipientPicture: recipient.recipientPicture,
                      amountToPay: recipient.amountToPay,
                    }}
                    currency={currency}
                  />
                ))}
              </div>
              <p className="text-sm text-black/50 flex gap-2 items-center">
                <Info className="w-4 min-w-4" />
                You are paying the total bill for all riders
              </p>
            </>
          )}
        </div>
        <hr className="my-6" />
        <p className="mb-4 text-sm text-black">Select payment method</p>
        {profileData?.id === organizer?.id && (
          <MarkPaymentMethod
            icon={IconBanknotes}
            type="cash"
            label="Mark all paid by cash"
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
          />
        )}
        {profileData?.id === organizer?.id && (
          <MarkPaymentMethod
            icon={IconCheckBook}
            type="check"
            label="Mark all paid by check"
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
          />
        )}
        {profileData?.id === organizer?.id && (
          <MarkPaymentMethod
            icon={IconPayPal}
            type="paypal"
            label="Mark all paid by PayPal"
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
          />
        )}
        {profileData?.id === organizer?.id && (
          <MarkPaymentMethod
            icon={IconVenmo}
            type="venmo"
            label="Mark all paid by Venmo"
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
          />
        )}
        <CreditCardPaymentMethod
          availablePaymentMethods={['cards', 'ach']}
          selectedPaymentMethod={null}
          selectedCard={selectedCard}
          setSelectedCard={setSelectedCard}
          user={user}
          organizer={organizer}
          openAddNewCardModal={openAddNewCardModal}
          openAddNewBankModal={openAddNewBankModal}
        />
      </div>
    </MainModal>
  )
}
